import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { Controller, useFormContext } from '@astro/forms'
import { Field } from './fields'

/* react-select skinned to match the .ar-input design language. */
export const selectStyles = {
  control: (base, state) => ({
    ...base,
    background: state.isFocused ? '#fff' : '#fafaf9',
    borderColor: state.isFocused ? 'rgba(217,119,6,.55)' : 'rgba(180,83,9,.16)',
    borderWidth: 1.5,
    borderRadius: 12,
    minHeight: 43,
    fontSize: 13.5,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(217,119,6,.09)' : 'none',
    transition: 'border-color .2s, box-shadow .2s, background .2s',
    cursor: 'pointer',
    ':hover': { borderColor: 'rgba(217,119,6,.4)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 13px' }),
  placeholder: (base) => ({ ...base, color: '#b5a896' }),
  singleValue: (base) => ({ ...base, color: '#1c0a04' }),
  input: (base) => ({ ...base, color: '#1c0a04' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#b45309', ':hover': { color: '#92400e' } }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: 'hidden',
    border: '1px solid rgba(180,83,9,.15)',
    boxShadow: '0 18px 44px -16px rgba(120,53,15,.35)',
    zIndex: 30,
  }),
  // The menu portals to <body> so the card's overflow-hidden can't clip it.
  // A portaled dropdown must outrank everything it can open on top of: the
  // sticky header (50), the suite's fixed mobile bottom nav (100), back-to-top
  // (200), the floating FABs (1000) and the dashboard's dialogs (1200) — a
  // select inside a dialog opened its menu BEHIND that dialog at the old 60.
  menuPortal: (base) => ({ ...base, zIndex: 1250 }),
  menuList: (base) => ({
    ...base,
    maxHeight: 340,
    padding: 6,
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(217,119,6,.4) transparent',
    '::-webkit-scrollbar': { width: 8 },
    '::-webkit-scrollbar-thumb': { background: 'rgba(217,119,6,.35)', borderRadius: 99 },
    '::-webkit-scrollbar-track': { background: 'transparent' },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: '10px 13px',
    backgroundColor: state.isSelected ? '#d97706' : state.isFocused ? 'rgba(245,158,11,.12)' : '#fff',
    color: state.isSelected ? '#fff' : '#1c0a04',
    cursor: 'pointer',
    ':active': { backgroundColor: 'rgba(217,119,6,.25)' },
  }),
}

/**
 * Form-bound react-select. `options` accepts plain strings or {value,label}.
 * `onChangeValue` fires after the form value updates (e.g. reset city on state change).
 * `creatable` lets the user type a value missing from the options list.
 */
export default function SelectField({
  name,
  label,
  hint,
  rules,
  options,
  placeholder = 'Select…',
  isLoading = false,
  isDisabled = false,
  creatable = false,
  onChangeValue,
}) {
  const { control } = useFormContext()
  const SelectComponent = creatable ? CreatableSelect : Select
  const normalized = options.map((o) =>
    typeof o === 'object' ? o : { value: String(o), label: String(o) },
  )
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <Field label={label} hint={hint} error={fieldState.error?.message}>
          <SelectComponent
            inputId={`select-${name}`}
            options={normalized}
            value={
              normalized.find((o) => o.value === field.value) ??
              (creatable && field.value ? { value: field.value, label: field.value } : null)
            }
            onChange={(option) => {
              const value = option?.value ?? ''
              field.onChange(value)
              onChangeValue?.(value)
            }}
            onBlur={field.onBlur}
            placeholder={placeholder}
            styles={selectStyles}
            isLoading={isLoading}
            isDisabled={isDisabled}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            formatCreateLabel={(input) => `Use "${input}"`}
          />
        </Field>
      )}
    />
  )
}
